import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { teachers, languages as languagesAPI, subscription } from '../../services/api';
import TrialStatusBanner from '../Trial/TrialStatusBanner';
import SubscriptionPaywallModal from '../Trial/SubscriptionPaywallModal';
import ChatWidget from '../Messages/ChatWidget';
import './TeacherSearchPage.css';
import '../Home/HomePage.css';

function TeacherSearchPage() {
  const navigate = useNavigate();
  const { user, logout, isStudent } = useAuth();
  const [teacherData, setTeacherData] = useState([]);
  const [teachersByCountry, setTeachersByCountry] = useState({});
  const [supportedLanguages, setSupportedLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [subscribeLoading, setSubscribeLoading] = useState(false);

  // Check if user is in active trial
  const isInTrial = () => {
    if (!user || user.hasPremium) return false;
    if (!user.trialEndsAt) return false;

    const trialEnd = new Date(user.trialEndsAt);
    const now = new Date();
    return trialEnd > now;
  };

  // Fetch supported languages on mount
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await languagesAPI.getSupported();
        // Handle different response structures
        const languages = Array.isArray(response.data)
          ? response.data
          : (response.data.languages || []);
        setSupportedLanguages(languages);
      } catch (err) {
        console.error('Failed to fetch languages:', err);
        setSupportedLanguages([]);
      }
    };
    fetchLanguages();
  }, []);

  // Fetch teachers when language selection changes
  useEffect(() => {
    const fetchTeachers = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await teachers.search(selectedLanguage);
        setTeacherData(response.data.teachers || []);
        setTeachersByCountry(response.data.teachersByCountry || {});
      } catch (err) {
        console.error('Failed to fetch teachers:', err);
        // If 404, it means endpoint not ready yet - show empty state
        if (err.response?.status === 404) {
          setTeacherData([]);
          setTeachersByCountry({});
        } else {
          setError('Failed to load teachers. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTeachers();
  }, [selectedLanguage]);

  const handleMessageTeacher = (teacher) => {
    // Navigate to messages page with teacher pre-selected
    navigate(`/messages?userId=${teacher.userID}`);
  };

  const handleViewProfile = (teacher) => {
    // Navigate to teacher profile page
    navigate(`/teacher/${teacher.userID}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/home');
  };

  const handleSubscribe = async () => {
    setSubscribeLoading(true);
    try {
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/find-teachers?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${baseUrl}/find-teachers?canceled=true`;

      const response = await subscription.createCheckoutSession({
        successUrl,
        cancelUrl,
      });

      window.location.href = response.data.sessionUrl;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      alert('Failed to start checkout. Please try again.');
      setSubscribeLoading(false);
    }
  };

  return (
    <div className="home-container">
      {/* Trial Status Banner - only for students in active trial */}
      {isStudent() && isInTrial() && (
        <TrialStatusBanner
          trialEndsAt={user.trialEndsAt}
          onUpgrade={handleSubscribe}
        />
      )}

      {/* Subscription Paywall Modal - blocks access when trial expires */}
      {isStudent() && showPaywallModal && (
        <SubscriptionPaywallModal
          onSubscribe={handleSubscribe}
          loading={subscribeLoading}
        />
      )}

      <header className="app-header">
        <div className="header-content">
          <div className="app-title" onClick={() => navigate('/home')} style={{ cursor: 'pointer' }}>
            <span className="app-icon-small">🌍</span>
            <h1>Teacha</h1>
          </div>

          <nav className="header-nav">
            <button
              className="nav-button active"
              onClick={() => navigate('/find-teachers')}
              title="Find teachers to practice with"
            >
              <span className="nav-icon">🔍</span>
              <span className="nav-label">Find Teachers</span>
            </button>
            <button
              className="nav-button"
              onClick={() => navigate('/progress')}
              title="View your progress and transcriptions"
            >
              <span className="nav-icon">📊</span>
              <span className="nav-label">Progress</span>
            </button>
            <button
              className="nav-button"
              onClick={() => navigate('/flashcards')}
              title="Review your flashcards"
            >
              <span className="nav-icon">🃏</span>
              <span className="nav-label">Flashcards</span>
            </button>
          </nav>

          <div className="header-user">
            <button
              className="user-button"
              onClick={() => setShowMenu(!showMenu)}
            >
              {user?.name || 'User'}
              <span className="dropdown-icon">▼</span>
            </button>

            {showMenu && (
              <div className="dropdown-menu">
                <button onClick={() => navigate('/profile')}>
                  Profile
                </button>
                {!user?.hasPremium && (
                  <button onClick={handleSubscribe}>
                    Upgrade to Premium
                  </button>
                )}
                <button onClick={handleLogout}>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="teacher-search-container">
          <div className="teacher-search-header">
            <h1>Find a Teacher</h1>
            <p className="subtitle">
              Browse native speaker teachers by language and location
            </p>
          </div>

        <div className="search-filters">
          <div className="filter-group">
            <label htmlFor="language-select">Filter by Language:</label>
            <select
              id="language-select"
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="language-select"
            >
              <option value="">All Languages</option>
              {supportedLanguages.map((lang) => (
                <option key={lang.id} value={lang.id}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading && (
          <div className="loading-state">
            <p>Loading teachers...</p>
          </div>
        )}

        {error && (
          <div className="error-state">
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && teacherData.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">👨‍🏫</div>
            <h3>We don't have teachers for this language yet</h3>
            <p>
              We're working hard to bring you the best native speaker teachers.
              Check back soon or try selecting a different language!
            </p>
          </div>
        )}

        {!loading && !error && teacherData.length > 0 && (
          <div className="teachers-by-country">
            {Object.entries(teachersByCountry)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([country, teachers]) => (
                <div key={country} className="country-section">
                  <h2 className="country-header">
                    {country} ({teachers.length})
                  </h2>
                  <div className="teachers-grid">
                    {teachers.map((teacher) => (
                      <div key={teacher.id} className="teacher-card">
                        <div className="teacher-photo">
                          {teacher.profilePhotoUrl ? (
                            <img
                              src={teacher.profilePhotoUrl}
                              alt={teacher.userName}
                            />
                          ) : (
                            <div className="teacher-photo-placeholder">
                              {teacher.userName.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="teacher-info">
                          <h3 className="teacher-name">{teacher.userName}</h3>
                          <p className="teacher-location">{teacher.country}</p>
                          {teacher.timezone && (
                            <p className="teacher-timezone">
                              Timezone: {teacher.timezone}
                            </p>
                          )}
                          {teacher.yearsExperience > 0 && (
                            <p className="teacher-experience">
                              {teacher.yearsExperience} years experience
                            </p>
                          )}
                          {teacher.languages && teacher.languages.length > 0 && (
                            <div className="teacher-languages">
                              {teacher.languages
                                .filter((lang) => lang.isTeachingLanguage)
                                .map((lang, idx) => (
                                  <span key={idx} className="language-badge">
                                    {lang.languageName} ({lang.proficiencyLevel})
                                  </span>
                                ))}
                            </div>
                          )}
                          {teacher.bio && (
                            <p className="teacher-bio">{teacher.bio}</p>
                          )}
                        </div>
                        <div className="teacher-actions">
                          <button
                            className="btn-primary"
                            onClick={() => handleMessageTeacher(teacher)}
                          >
                            Send Message
                          </button>
                          <button
                            className="btn-secondary"
                            onClick={() => handleViewProfile(teacher)}
                          >
                            View Profile
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
        </div>
      </main>

      {/* Chat Widget - Floating in bottom right */}
      <ChatWidget />
    </div>
  );
}

export default TeacherSearchPage;
